import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { API } from "../lib/api";

// Convert a TerminalValue.io path/URL into the matching backend
// /api/share/* endpoint (which renders OG-tagged HTML the same way
// social crawlers see it).
function toSharePath(input) {
  let p = (input || "").trim();
  if (!p) return null;
  // Strip protocol + host, leaving just the path.
  p = p.replace(/^https?:\/\/[^/]+/i, "");
  // Drop query + hash — they're not part of the share-endpoint key.
  p = p.split("?")[0].split("#")[0];
  if (!p.startsWith("/")) p = "/" + p;
  // Drop /index.html and trailing slash.
  p = p.replace(/\/index\.html$/, "").replace(/\/$/, "");
  if (!p || p === "") return "/api/share";
  // Whitelisted route patterns.
  if (
    /^\/tool\/[^/]+$/.test(p) ||
    /^\/c\/[^/]+$/.test(p) ||
    /^\/c\/[^/]+\/[^/]+$/.test(p) ||
    /^\/f\/[^/]+$/.test(p)
  ) {
    return "/api/share" + p;
  }
  return null;
}

function extractMeta(html) {
  // Quick & dirty meta tag parser. The /api/share routes emit a small,
  // predictable HTML doc so regex is safer than spinning up DOMParser
  // (which mangles <meta> in some browsers without a full document).
  const get = (prop) => {
    const re = new RegExp(
      `<meta[^>]+(?:property|name)=["']${prop}["'][^>]*content=["']([^"']+)["']`,
      "i",
    );
    const m = html.match(re);
    if (m) return m[1];
    // Try content-first ordering too.
    const re2 = new RegExp(
      `<meta[^>]+content=["']([^"']+)["'][^>]*(?:property|name)=["']${prop}["']`,
      "i",
    );
    const m2 = html.match(re2);
    return m2 ? m2[1] : "";
  };
  return {
    title: get("og:title") || get("twitter:title"),
    description: get("og:description") || get("twitter:description"),
    image: get("og:image") || get("twitter:image"),
    url: get("og:url"),
  };
}

function decodeEntities(s) {
  return (s || "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

export default function AdminOgPreview({ initialPath }) {
  const [input, setInput] = useState(initialPath || "/");
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [bust, setBust] = useState(0); // forces <img> refetch

  const sharePath = useMemo(() => toSharePath(input), [input]);

  const load = async (overridePath) => {
    const path = overridePath !== undefined ? toSharePath(overridePath) : sharePath;
    if (!path) {
      setErr("Unrecognized path. Try /, /tool/<slug>, /c/<asset>, /f/<fn>, or /c/<asset>/<fn>.");
      setMeta(null);
      return;
    }
    setLoading(true);
    setErr("");
    try {
      // The share endpoint is mounted under the FastAPI router at
      // /api/share/...; path already includes that prefix, so we
      // hit BACKEND_URL + path.
      const res = await fetch(API.replace(/\/api$/, "") + path, {
        headers: { "User-Agent": "TerminalValueOgPreview/1.0" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const m = extractMeta(html);
      setMeta({
        title: decodeEntities(m.title),
        description: decodeEntities(m.description),
        image: decodeEntities(m.image),
        url: decodeEntities(m.url),
      });
      setBust((b) => b + 1);
    } catch (e) {
      setErr(`Fetch failed: ${e.message}`);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-load on first mount AND whenever the parent feeds in a fresh
  // initialPath (e.g. admin clicks "[og]" on a different tool row).
  // The parent may append `?_=<ts>` to force this effect to re-fire
  // when re-selecting the same tool; we strip that before displaying.
  useEffect(() => {
    if (initialPath) {
      const clean = initialPath.split("?")[0];
      setInput(clean);
      load(initialPath);
    } else {
      load("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPath]);

  const canonicalUrl = meta?.url || "";

  const validators = canonicalUrl
    ? [
        {
          label: "X Card Validator",
          // X retired the validator; the closest replacement is to paste
          // into a compose window. We deep-link to compose with the URL
          // pre-filled, which forces X to refresh its OG cache on send.
          href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(canonicalUrl)}`,
          testid: "validator-x",
        },
        {
          label: "LinkedIn Post Inspector",
          href: `https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(canonicalUrl)}`,
          testid: "validator-linkedin",
        },
        {
          label: "Facebook Sharing Debugger",
          href: `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(canonicalUrl)}`,
          testid: "validator-fb",
        },
      ]
    : [];

  const copy = (text, label) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error("Copy failed"));
  };

  const imageSrc = meta?.image ? `${meta.image}${meta.image.includes("?") ? "&" : "?"}t=${bust}` : "";

  return (
    <div data-testid="admin-og-preview" className="space-y-4">
      <div className="text-[13px] text-[#555]">
        Paste any TerminalValue.io path (e.g. <code>/</code>, <code>/tool/deepvest</code>,
        <code> /c/equities</code>, <code>/f/screeners</code>,
        <code> /c/equities/screeners</code>) and see exactly what X / LinkedIn / Slack will scrape.
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="/tool/<slug> or full https://terminalvalue.io/..."
          className="tv-input flex-1 min-w-[260px]"
          data-testid="og-preview-input"
        />
        <button
          className="tv-cat-pill active"
          onClick={load}
          disabled={loading}
          data-testid="og-preview-load-btn"
        >
          [{loading ? "loading…" : "preview"}]
        </button>
      </div>

      {err && (
        <div className="text-[12px] text-red-700 border border-red-700 px-2 py-1" data-testid="og-preview-error">
          {err}
        </div>
      )}

      {meta && !err && (
        <div className="border border-black p-4 space-y-3" data-testid="og-preview-result">
          <div className="text-[11px] text-[#888]">
            share-endpoint: <code>{sharePath}</code>
          </div>

          {imageSrc ? (
            <a
              href={imageSrc}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="og-preview-image-link"
            >
              <img
                src={imageSrc}
                alt="OG card preview"
                className="border border-black w-full max-w-[600px]"
                style={{ aspectRatio: "1200/630" }}
                data-testid="og-preview-image"
              />
            </a>
          ) : (
            <div className="text-[12px] text-[#888]">No og:image found.</div>
          )}

          <dl className="text-[12px] grid grid-cols-[120px_1fr] gap-y-1 gap-x-3">
            <dt className="text-[#888]">og:title</dt>
            <dd className="font-bold" data-testid="og-meta-title">{meta.title || "—"}</dd>
            <dt className="text-[#888]">og:description</dt>
            <dd data-testid="og-meta-description">{meta.description || "—"}</dd>
            <dt className="text-[#888]">og:url</dt>
            <dd className="break-all" data-testid="og-meta-url">{meta.url || "—"}</dd>
            <dt className="text-[#888]">og:image</dt>
            <dd className="break-all" data-testid="og-meta-image">{meta.image || "—"}</dd>
          </dl>

          <div className="flex flex-wrap gap-2 pt-1">
            {canonicalUrl && (
              <button
                className="tv-cat-pill"
                onClick={() => copy(canonicalUrl, "URL")}
                data-testid="copy-url-btn"
              >
                [copy url]
              </button>
            )}
            {meta.image && (
              <button
                className="tv-cat-pill"
                onClick={() => copy(meta.image, "Image URL")}
                data-testid="copy-image-btn"
              >
                [copy image url]
              </button>
            )}
            <button
              className="tv-cat-pill"
              onClick={() => {
                setBust((b) => b + 1);
                toast.success("image refetched");
              }}
              data-testid="refresh-image-btn"
              title="Re-pull the OG image (bypasses browser cache)"
            >
              [refresh image]
            </button>
          </div>

          {validators.length > 0 && (
            <div className="pt-2 border-t border-[#eee]">
              <div className="text-[11px] text-[#888] mb-1">force re-scrape:</div>
              <div className="flex flex-wrap gap-3 text-[12px]">
                {validators.map((v) => (
                  <a
                    key={v.label}
                    href={v.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tv-link"
                    data-testid={v.testid}
                  >
                    [{v.label}]
                  </a>
                ))}
              </div>
              <div className="text-[11px] text-[#888] mt-2 leading-snug">
                X caches OG cards for ~7 days. Posting from the compose link above (or any new
                share) will force X to refetch the latest <code>?v=</code> URL. LinkedIn /
                Facebook debuggers have explicit &quot;scrape again&quot; buttons on their pages.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
